import { useState, useRef, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import download from 'downloadjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilePdf, 
  faCloudUploadAlt, 
  faTimes, 
  faFileAlt, 
  faCut, 
  faRedo 
} from '@fortawesome/free-solid-svg-icons';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { useTheme } from '../../context/ThemeContext';

export default function PDFSplitterPro() {
  const [pdfDoc, setPdfDoc] = useState(null);
  const { darkMode } = useTheme();
  const [totalPages, setTotalPages] = useState(0);
  const [fileName, setFileName] = useState('');
  const [extractMode, setExtractMode] = useState('specific');
  const [pagesInput, setPagesInput] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);

  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0] || (e?.dataTransfer?.files?.[0]);
    if (!file || file.type !== 'application/pdf') return;

    setFileName(file.name);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      setPdfDoc(pdf);
      setTotalPages(pdf.getPageCount());
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try another file.');
      clearSelectedFile();
    }
  };

  // Clear selected file
  const clearSelectedFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileName('');
    setPdfDoc(null);
    setTotalPages(0);
    setSelectedPages([]);
  };

  // Update extract mode
  const updateExtractMode = (mode) => {
    setExtractMode(mode);
    setPagesInput('');
    setRangeStart('');
    setRangeEnd('');
  };

  // Validate pages input
  const validatePagesInput = (value) => {
    // Remove any invalid characters
    const cleanedValue = value.replace(/[^0-9,\-]/g, '');
    setPagesInput(cleanedValue);
    
    // Update selected pages for preview
    if (cleanedValue) {
      const pages = parsePagesInput(cleanedValue);
      setSelectedPages(pages);
    } else {
      setSelectedPages([]);
    }
  };

  // Update range end input
  const updateRangeEnd = (start) => {
    setRangeStart(start);
    if (start && !rangeEnd) {
      // Auto-set rangeEnd if not set
      setRangeEnd(Math.min(parseInt(start) + 1, totalPages).toString());
    }
  };

  // Validate range inputs
  const validateRange = (start, end) => {
    if (start && end) {
      if (parseInt(start) > parseInt(end)) {
        setRangeEnd(start);
      }
    }
  };

  // Parse pages input into array of page numbers (1-based)
  const parsePagesInput = (input = pagesInput) => {
    const mode = extractMode;
    let pages = [];
    
    if (mode === 'specific') {
      if (!input.trim()) return [];
      
      const parts = input.split(',');
      for (const part of parts) {
        if (part.includes('-')) {
          const [start, end] = part.split('-').map(Number);
          for (let i = start; i <= end; i++) {
            if (i >= 1 && i <= totalPages) {
              pages.push(i);
            }
          }
        } else {
          const page = parseInt(part);
          if (page >= 1 && page <= totalPages) {
            pages.push(page);
          }
        }
      }
    } 
    else if (mode === 'range') {
      const start = parseInt(rangeStart) || 1;
      const end = parseInt(rangeEnd) || totalPages;
      
      for (let i = start; i <= end; i++) {
        if (i >= 1 && i <= totalPages) {
          pages.push(i);
        }
      }
    }
    else if (mode === 'odd') {
      for (let i = 1; i <= totalPages; i += 2) {
        pages.push(i);
      }
    }
    else if (mode === 'even') {
      for (let i = 2; i <= totalPages; i += 2) {
        pages.push(i);
      }
    }
    
    // Remove duplicates and sort
    return [...new Set(pages)].sort((a, b) => a - b);
  };

  // Toggle page selection
  const togglePageSelection = (page) => {
    let newSelectedPages;
    if (selectedPages.includes(page)) {
      newSelectedPages = selectedPages.filter(p => p !== page);
    } else {
      newSelectedPages = [...selectedPages, page].sort((a, b) => a - b);
    }
    setSelectedPages(newSelectedPages);
    setPagesInput(newSelectedPages.join(','));
  };

  // Split PDF function
  const splitPDF = async () => {
    if (!pdfDoc) {
      alert('Please select a PDF file first.');
      return;
    }
    
    const pages = parsePagesInput();
    if (pages.length === 0) {
      alert('Please select at least one page to extract.');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Create new PDF
      const newPdf = await PDFDocument.create();
      
      // Copy selected pages (convert to 0-based)
      const copiedPages = await newPdf.copyPages(pdfDoc, pages.map(p => p - 1));
      copiedPages.forEach(page => newPdf.addPage(page));
      
      // Save and download
      const pdfBytes = await newPdf.save();
      const outputFileName = fileName.replace('.pdf', '') + '_extracted.pdf';
      
      download(pdfBytes, outputFileName, 'application/pdf');
      
    } catch (error) {
      console.error('Error splitting PDF:', error);
      alert('An error occurred while splitting the PDF. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset form
  const resetForm = () => {
    clearSelectedFile();
    setExtractMode('specific');
    setPagesInput('');
    setRangeStart('');
    setRangeEnd('');
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e);
  };

  // Set max range when totalPages changes
  useEffect(() => {
    if (totalPages > 0 && rangeEnd > totalPages) {
      setRangeEnd(totalPages.toString());
    }
  }, [totalPages, rangeEnd]);

  return (
    <>
      <HeaderSection toolId="splitPDF" />
      <div className={`max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`rounded-xl shadow-md overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700 shadow-gray-900/10' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFilePdf} className="mr-3" />
              PDF Splitter Pro
            </h1>
            <p className="opacity-90">
              Extract specific pages or ranges from your PDF documents
            </p>
          </div>

          {/* Upload Area */}
          <section 
            ref={uploadAreaRef}
            className={`border-2 border-dashed rounded-xl py-10 px-4 md:px-20 m-6 text-center cursor-pointer transition-all duration-300 ${
              isDragging 
                ? 'border-green-500 bg-green-100 dark:bg-green-900/30' 
                : fileName 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-300 hover:border-green-400 hover:bg-green-50 dark:hover:bg-gray-700'
            }`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon 
              icon={faCloudUploadAlt} 
              className={`text-4xl md:text-5xl mb-4 p-6 transition-transform duration-300 hover:scale-110 ${
                darkMode ? 'text-green-400' : 'text-green-500'
              }`} 
            />
            <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
              Drag & drop your PDF file here or click to browse
            </p>
            <input 
              type="file" 
              ref={fileInputRef}
              className="hidden" 
              accept="application/pdf"
              onChange={handleFileSelect}
            />
            {fileName && (
              <div className="mt-4 text-sm font-medium flex items-center justify-center gap-2">
                <span className={darkMode ? 'text-gray-200' : 'text-green-600'}>{fileName}</span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelectedFile();
                  }}
                  className={`transition-colors duration-300 ${
                    darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </section>

          {/* Controls Section */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 m-6">
            <div className="space-y-2">
              <label htmlFor="extractMode" className={`block text-sm font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Extract Mode:
              </label>
              <select
                id="extractMode"
                className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-800'
                }`}
                value={extractMode}
                onChange={(e) => updateExtractMode(e.target.value)}
              >
                <option value="specific">Specific Pages</option>
                <option value="range">Page Range</option>
                <option value="odd">Odd Pages</option>
                <option value="even">Even Pages</option>
              </select>
            </div>

            {extractMode === 'specific' && (
              <div className="space-y-2">
                <label htmlFor="pages" className={`block text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Pages to Extract:
                </label>
                <input
                  type="text"
                  id="pages"
                  className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                  placeholder="e.g. 1,3,5-7"
                  value={pagesInput}
                  onChange={(e) => validatePagesInput(e.target.value)}
                />
              </div>
            )}

            {extractMode === 'range' && (
              <div className="col-span-2 space-y-2">
                <label className={`block text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Page Range:
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className={`flex-1 border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-800'
                    }`}
                    placeholder="Start"
                    min="1"
                    max={totalPages}
                    value={rangeStart}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateRangeEnd(value);
                      validateRange(value, rangeEnd);
                    }}
                  />
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>to</span>
                  <input
                    type="number"
                    className={`flex-1 border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-800'
                    }`}
                    placeholder={`Max ${totalPages}`}
                    min={rangeStart || 1}
                    max={totalPages}
                    value={rangeEnd}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRangeEnd(value);
                      validateRange(rangeStart, value);
                    }}
                  />
                </div>
              </div>
            )}
          </section>

          {/* Page Preview Section */}
          {totalPages > 0 && (
            <section className={`border rounded-lg p-4 mx-6 mb-6 transition-colors duration-300 ${
              darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <h3 className={`text-sm font-medium flex items-center gap-2 mb-3 ${
                darkMode ? 'text-green-400' : 'text-green-600'
              }`}>
                <FontAwesomeIcon icon={faFileAlt} />
                Document Preview
              </h3>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`px-3 py-1 text-xs rounded-md transition-all duration-300 ${
                      selectedPages.includes(page)
                        ? 'bg-green-600 text-white shadow-md'
                        : darkMode
                          ? 'bg-gray-600 text-gray-200 hover:bg-green-700 hover:text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-green-500 hover:text-white'
                    }`}
                    onClick={() => togglePageSelection(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Action Buttons */}
          <footer className="flex flex-col sm:flex-row gap-3 m-6">
            <button
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                pdfDoc 
                  ? darkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-70 text-gray-600 dark:text-gray-300'
              }`}
              onClick={splitPDF}
              disabled={!pdfDoc || isProcessing}
            >
              {isProcessing ? (
                <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCut} />
                  Split PDF
                </>
              )}
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                darkMode
                  ? 'border border-green-400 text-green-400 hover:bg-gray-700'
                  : 'border border-green-500 text-green-600 hover:bg-green-50'
              }`}
              onClick={resetForm}
            >
              <FontAwesomeIcon icon={faRedo} />
              Reset
            </button>
          </footer>
        </div>
      </div>
      <BottomSection toolId="splitPDF" />
    </>
  );
}